import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import {
  Box,
  CircularProgress,
  Container,
  FormControl,
  InputLabel,
  Typography,
} from '@mui/material';
import React, { useMemo } from 'react';
import { Criteria } from '../../types';
import SearchableSelect from './SearchableSelect';

interface Props {
  title: string;
  criteriaList: Criteria[];
  selectedCriteriaId: string;
  onCriteriaChange: (criteriaId: string) => void;
  placeholderText: string;
  generating: boolean;
  generateError: string;
  hasSuggestion: boolean;
  children: React.ReactNode;
}

const SuggestionPageShell: React.FC<Props> = ({
  title,
  criteriaList,
  selectedCriteriaId,
  onCriteriaChange,
  placeholderText,
  generating,
  generateError,
  hasSuggestion,
  children,
}) => {
  const criteriaOptions = useMemo(
    () =>
      [...criteriaList]
        .filter(c => !c.isArchived || c.id === selectedCriteriaId)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(c => ({ value: c.id, label: c.name })),
    [criteriaList, selectedCriteriaId]
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 2, height: 'calc(100vh - 96px)' }}>
      <Typography variant="h4" component="h1" sx={{ mb: 2 }}>
        {title}
      </Typography>
      <Box sx={{ mb: 2, maxWidth: 400 }}>
        <FormControl fullWidth size="small">
          <InputLabel>Criterion</InputLabel>
          <SearchableSelect
            value={selectedCriteriaId}
            label="Criterion"
            options={criteriaOptions}
            onChange={e => onCriteriaChange(e.target.value as string)}
          />
        </FormControl>
      </Box>

      {!selectedCriteriaId ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: 'calc(100% - 56px)',
          }}
        >
          <Typography variant="body1" color="text.secondary">
            {placeholderText}
          </Typography>
        </Box>
      ) : !hasSuggestion ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 8 }}>
          {generating ? (
            <>
              <CircularProgress sx={{ mb: 1.5 }} />
              <Typography variant="body1" color="text.secondary">
                Generating suggestion...
              </Typography>
            </>
          ) : (
            <>
              <CheckCircleOutlineIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1.5 }} />
              <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                No pending suggestions
              </Typography>
            </>
          )}
          {generateError && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              {generateError}
            </Typography>
          )}
        </Box>
      ) : (
        children
      )}
    </Container>
  );
};

export default SuggestionPageShell;
